import { z } from 'zod';
import type { ContentI18n, QuestionResponseType } from '@/shared/types/nps';
import { compactContentI18n } from '../model/content-i18n';

const optionLabelsSchema = z
  .object({
    'pt-PT': z.string().optional(),
    en: z.string().optional(),
    es: z.string().optional(),
  })
  .optional();

const selectOptionSchema = z.object({
  label: z.string().min(1, 'Informe o rótulo'),
  value: z
    .string()
    .min(1, 'Informe o valor')
    .trim()
    .refine((v) => !v.includes(' '), 'Valor não pode conter espaços'),
  labels: optionLabelsSchema,
});

const selectMetadataSchema = z.object({
  options: z
    .array(selectOptionSchema)
    .min(1, 'Adicione ao menos uma opção')
    .superRefine((options, ctx) => {
      const seen = new Set<string>();
      options.forEach((opt, index) => {
        if (seen.has(opt.value)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: 'Valores das opções devem ser únicos',
            path: [index, 'value'],
          });
        } else {
          seen.add(opt.value);
        }
      });
    }),
});

const localizedTextSchema = z.object({
  title: z.string().optional(),
  description: z.string().optional(),
});

const contentI18nSchema = z
  .object({
    'pt-PT': localizedTextSchema.optional(),
    en: localizedTextSchema.optional(),
    es: localizedTextSchema.optional(),
  })
  .optional();

export const questionSchema = z
  .object({
    title: z.string().min(1, 'Informe a pergunta'),
    description: z.string().min(1, 'Informe a descrição'),
    i18n: contentI18nSchema,
    required: z.boolean().default(false),
    response_type: z.enum(['STAR', 'RANGE', 'BOOLEAN', 'TEXT', 'SELECT', 'DATE', 'CHECKBOX']),
    response_metadata: z.record(z.unknown()).optional(),
  })
  .superRefine((data, ctx) => {
    if (data.response_type === 'SELECT' || data.response_type === 'CHECKBOX') {
      const parsed = selectMetadataSchema.safeParse(data.response_metadata);
      if (!parsed.success) {
        parsed.error.issues.forEach((issue) => ctx.addIssue({ ...issue, path: ['response_metadata', ...issue.path] }));
      }
    }
  });

export type QuestionFormValues = z.infer<typeof questionSchema>;

export function defaultQuestionMetadata(type: QuestionResponseType) {
  if (type === 'RANGE') return { min: 0, max: 10 };
  if (type === 'STAR') return { max: 5 };
  if (type === 'TEXT') return { input_type: 'input' as const };
  if (type === 'SELECT' || type === 'CHECKBOX') return { options: [{ label: 'Opção 1', value: 'opcao_1' }] };
  return {};
}

export function toQuestionPayload(values: QuestionFormValues) {
  const i18n = compactContentI18n(values.i18n as ContentI18n | undefined) ?? {};
  return {
    title: values.title,
    description: values.description,
    i18n,
    required: values.required,
    response_type: values.response_type,
    response_metadata: values.response_metadata,
  };
}
